/**
 * `trackLegacyEvent()` — the v1 (Segment / PostHog / Datadog) sender as a plain
 * function, outside the Rematch model.
 *
 * This is the body of `analyticsScribe.trackEvent` moved verbatim: same props,
 * same order, same three destinations, same `$groups` / `pageUrl` /
 * `installer_type` handling. The model effect is now a one-line delegate, so
 * every existing dispatch caller is unaffected and the two calling conventions
 * cannot drift.
 *
 * It exists so that a call site can emit the v1 pair of a legacy event without
 * holding a Rematch `dispatch` — the model wrappers that composed `trackEvent`
 * + `trackEventV2` become plain functions (see `./commerce/checkout-events`).
 *
 * NEW EVENTS DO NOT BELONG HERE. The v1 path is deprecated; new events call
 * `track()` from `./track` (see `.ai-context/analytics.md`).
 */
import {
  getAppType,
  getInstallSourceAnalyticsTraits,
  getPlatform,
  getScreenDimensions,
  segmentClient,
} from '@/lib/analytics/legacy-sender-context';
import { type SegmentObjectName } from '@/models/analytics/segment-names';
import { type TrackingProps } from '@/models/analyticsScribeModel';
// `from_integration` is enrichment the v1 payload has always carried, and the
// integration atoms are the only source for it. Read through the getter rather
// than a hook: this is a plain function with no React context, and moving the
// read to the caller would put it at every call site instead of one.
import { getIntegrationEhrProvider } from '@/atoms/integrationAtoms';
import { getDesktopInstallerType } from '@/utils/appConfiguration';
import { datadogRum } from '@datadog/browser-rum';
import { getAnalyticsContext } from '@heidi/runtime/analytics';
import posthog from 'posthog-js';
import { browserName, deviceType, osName } from 'react-device-detect';

export function trackLegacyEvent<T extends TrackingProps>(input: {
  name: SegmentObjectName;
  props: T;
  /** Optional URL to override page URL in analytics (useful for preserving attribution params) */
  pageUrl?: string | null;
}): void {
  const { name, props, pageUrl } = input;
  const currentTeamId = getAnalyticsContext().teamId;

  const baseProps: Record<string, unknown> = {
    deviceType,
    osName,
    browserName,
    ...getScreenDimensions(),
    eventName: props.eventName || `${name} ${props.action}`,
    from_integration: getIntegrationEhrProvider(),
    App: getAppType(),
    app_type: getAppType(),
    client_platform: getAppType(),
    platform: getPlatform(),
    ...props,
    // Placed after the props spread so the system value always wins
    // over any accidental `installer_type` key in caller-supplied props.
    installer_type: getDesktopInstallerType(),
    ...getInstallSourceAnalyticsTraits(),
  };

  const segmentProps = {
    ...baseProps,
    ...(currentTeamId && {
      $groups: {
        segment_group: currentTeamId,
      },
    }),
  };

  // Include attributed URL in PostHog props if provided
  const posthogProps = pageUrl
    ? { ...baseProps, $current_url: pageUrl, attributed_url: pageUrl }
    : baseProps;
  posthog.capture(`${name}_${props.action}`, posthogProps);

  datadogRum.addAction(`${name}_${props.action}`, {
    ...baseProps,
    teamId: currentTeamId,
    timestamp: new Date().toISOString(),
    source: 'rum_action',
    // Include attributed URL if provided
    ...(pageUrl && { attributed_url: pageUrl }),
  });

  // Pass custom context to override page URL if provided (preserves attribution params after OAuth redirects)
  const segmentOptions = pageUrl ? { context: { page: { url: pageUrl } } } : undefined;

  void segmentClient.track(`${name}_${props.action}`, segmentProps, segmentOptions);
}
